import { Router } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import {
  createTheme,
  responsiveFontSizes,
  StyledEngineProvider,
  Theme,
  ThemeProvider,
} from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { LicenseInfo as MuiLicenseInfo } from '@mui/x-license-pro';
import Analytics from 'analytics';
import { AnalyticsProvider } from 'use-analytics';
import userflow from 'userflow.js';

import client from './utils/client';
import { AnalyticsEvent, NodeEnvironment } from './utils/enum';
import browserHistory from './utils/history';
import Routes from './routes';
import { consolePlugin, mixpanelPlugin } from './ui-analytics';

declare module '@mui/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}
let theme = createTheme({
  palette: {
    primary: {
      main: '#110b5c',
    },
    secondary: {
      main: '#F5DE76',
    },
  },
  typography: {
    fontFamily: ['"Soleil"', 'sans-serif'].join(','),
    button: {
      textTransform: 'none',
    },
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#5ED1F0',
          },
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme);

if (process.env.REACT_APP_MUI_LICENSE_KEY) {
  MuiLicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY);
} else {
  console.log('Warning: No MUI License Key.');
}

if (
  process.env.REACT_APP_USERFLOW_TOKEN &&
  (process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT ||
    process.env.REACT_APP_NODE_ENV === NodeEnvironment.PROD)
) {
  userflow.init(process.env.REACT_APP_USERFLOW_TOKEN);
}

const analyticsPlugins: Record<string, unknown>[] = [];

if (process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV) {
  analyticsPlugins.push(
    consolePlugin({
      pageEvent: AnalyticsEvent.PAGE_VIEW,
    }),
  );
}

if (
  process.env.REACT_APP_MIXPANEL_TOKEN &&
  (process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT ||
    process.env.REACT_APP_NODE_ENV === NodeEnvironment.PROD)
) {
  analyticsPlugins.push(
    mixpanelPlugin({
      token: process.env.REACT_APP_MIXPANEL_TOKEN,
    }),
  );
}

const analytics = Analytics({
  app: 'trustate-app',
  plugins: analyticsPlugins.length ? analyticsPlugins : undefined,
});

function App(): JSX.Element {
  return (
    <ApolloProvider client={client}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <AnalyticsProvider instance={analytics}>
              <Router history={browserHistory}>
                <Routes />
              </Router>
            </AnalyticsProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ApolloProvider>
  );
}

export default App;
